import { React, useState, useEffect, useContext, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { DashCircle, Download, Copy, PlusCircle, Gear, Box, ChevronDown } from 'react-bootstrap-icons';
import { Select, Popover, Flex, Spin, Alert, Input, Button, Collapse, Popconfirm } from 'antd';
import { CopyOutlined, DeleteOutlined, InfoCircleFilled, LoadingOutlined, PauseCircleOutlined, PlayCircleOutlined } from '@ant-design/icons';

import colors from '../../styles/_colors.scss';

import { TimezoneDropdown } from '../../components/elements/TimezoneDropdown';
import GoogleCloudConfigForm from './GoogleCloudConfigFile';

import ConfigService from '../../services/config.service';


const { Option } = Select;

const _ = require('lodash');

const checkEmptyArray = (arr) => {
  const check =
    !arr ||
    arr.length === 0 ||
    arr.filter((elem) => Object.keys(elem).length !== 0).length === 0;

  return check;
}

const copyToClipboard = (text) => navigator.clipboard.writeText(text?.current?.value);

export const ProductSettingsModal = ({ context }) => {
  const {
    productDescLink,
    timezones,
    timezoneName,
    curTimezone,
    companyDomains,
    setConfigParams,
    setModalSaveStatus,
  } = useContext(context);

  const [choosenTimezone, setChoosenTimezone] = useState(curTimezone);
  const [curCompanyDomains, setCurCompanyDomains] = useState(companyDomains.length ? companyDomains : [{}]);

  const [configParamsToSave, setConfigParamsToSave] = useState({});

  const [showGuideDivDomain, setShowGuideDivDomain] = useState(false);


  useEffect(() => {
    setChoosenTimezone(curTimezone);
    setCurCompanyDomains(companyDomains);
  }, [curTimezone, companyDomains]);


  const handleTimezoneChange = (newTimezone) => setChoosenTimezone(newTimezone);


  const handleElemDelete = (idx) => {
    const newDomainsList = curCompanyDomains?.filter((elem, index) => index !== idx);
    setCurCompanyDomains(newDomainsList.length ? newDomainsList : [{}]);
  }

  const handleElemAdd = () => {
    setCurCompanyDomains([...curCompanyDomains, {}]);
  }

  const handleInputValueChange = (idx, field, event) => {
    const newDomainsList = [...curCompanyDomains];
    const elem = { ...newDomainsList[idx] };
    elem[field] = event.target.value;
    newDomainsList[idx] = elem;
    setCurCompanyDomains(newDomainsList);
  }

  const isDomainEmpty = (domain) => {
    return Object.values(domain).every(val => !val || val.trim() === '');
  };

  const filterEmptyDomains = (domains) =>
    domains.filter(domain => !isDomainEmpty(domain));

  useEffect(() => {
    setConfigParamsToSave(prevParams => ({
      ...prevParams,
      timezone: choosenTimezone,
      company_domains: curCompanyDomains,
    }));

    // Filter out empty domain objects for both current and initial state
    const filteredCurDomains = filterEmptyDomains(curCompanyDomains);
    const filteredInitialDomains = filterEmptyDomains(companyDomains);

    // Check if meaningful changes exist
    const domainsChanged =
      JSON.stringify(filteredCurDomains) !== JSON.stringify(filteredInitialDomains);


    setModalSaveStatus(domainsChanged || choosenTimezone !== curTimezone);
  }, [choosenTimezone, curCompanyDomains]);




  useEffect(() => setConfigParams(configParamsToSave), [configParamsToSave]);


  return (
    <div className="modal-elems-rows">
      <div>
        <h2 className='header-letter-spacing'>General settings</h2>
      </div>
      <div className="container-guide modal-panel-button-row">
        <Link className="modal-guide-banner blue-bg" target="_blank" to={productDescLink}>
          <div className="file-icon">
            <svg width="26" height="31" viewBox="0 0 26 31" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g filter="url(#filter0_d_190_157)">
                <path d="M2.64922 3.41406H16.4948V7.60147C16.4948 7.95999 16.7855 8.25062 17.144 8.25062H19.3979V26.6164C19.3979 26.975 19.1073 27.2656 18.7488 27.2656H2.64922C2.2907 27.2656 2.00006 26.975 2.00006 26.6164V4.06322C2.00006 3.7047 2.2907 3.41406 2.64922 3.41406Z" fill="#69C4FF" fillOpacity="0.5" />
                <path d="M3.72516 24.6433H16.4543C16.4543 24.8469 16.6193 25.0119 16.8228 25.0119H19.4347V25.8118C19.4347 26.1703 19.1441 26.4609 18.7856 26.4609H4.37431C4.0158 26.4609 3.72516 26.1703 3.72516 25.8118V24.6433Z" fill="#69C4FF" fillOpacity="0.5" />
                <path d="M4.34194 1.79309H18.3155V5.98808C18.3155 6.3466 18.6061 6.63724 18.9646 6.63724H21.7393V25.0329C21.7393 25.3914 21.4487 25.682 21.0902 25.682H4.34194C3.98342 25.682 3.69278 25.3914 3.69278 25.0329V2.44225C3.69278 2.08373 3.98342 1.79309 4.34194 1.79309Z" fill="#DBF1FF" />
                <path d="M5.41306 22.566H18.6682C18.6682 22.755 18.8214 22.9082 19.0104 22.9082H21.7718V23.6046C21.7718 23.9631 21.4811 24.2538 21.1226 24.2538H6.06221C5.70369 24.2538 5.41306 23.9631 5.41306 23.6046V22.566Z" fill="#B0E0FF" />
                <path d="M6.06221 0.170166H18.7207V4.25985C18.7207 4.61837 19.0114 4.909 19.3699 4.909H23.46V22.6673C23.46 23.1492 23.0694 23.5398 22.5875 23.5398H6.06221C5.70369 23.5398 5.41306 23.2491 5.41306 22.8906V0.819322C5.41306 0.460803 5.70369 0.170166 6.06221 0.170166Z" fill="white" />
                <line x1="8.82252" y1="9.40248" x2="19.988" y2="9.40248" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                <line x1="8.90306" y1="15.1451" x2="20.0685" y2="15.1451" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                <line x1="8.82252" y1="11.3499" x2="19.988" y2="11.3499" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                <line x1="8.90306" y1="17.0925" x2="20.0685" y2="17.0925" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                <line x1="8.82252" y1="13.1675" x2="19.988" y2="13.1675" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                <path d="M19.3703 4.946C19.0118 4.94601 18.7212 4.65537 18.7212 4.29685L18.7212 0.170195L23.4599 4.94597L19.3703 4.946Z" fill="#69C4FF" fillOpacity="0.14" />
              </g>
              <defs>
                <filter id="filter0_d_190_157" x="0.255097" y="0.170166" width="24.9498" height="30.5854" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                  <feOffset dy="1.74496" />
                  <feGaussianBlur stdDeviation="0.872482" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix type="matrix" values="0 0 0 0 0.168627 0 0 0 0 0.168627 0 0 0 0 0.709804 0 0 0 0.15 0" />
                  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_190_157" />
                  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_190_157" result="shape" />
                </filter>
              </defs>
            </svg>

          </div>
          <div className="panel-button-text">
            <div className="panel-button-desc">Guide how to set up in Tag Manager</div>
          </div>
        </Link>
      </div>
      <div>
        <p style={{
          marginBottom: 0,
          maxWidth: '42rem',
          width: '100%',
          overflowWrap: 'break-word'
        }}>
          Configure the settings below to start the configuration of the Tag Monitor.
          These are basic settings which will reflect on the complete implementation.
        </p>
      </div>
      <div style={{ position: 'relative' }} className="elems-list">
        {showGuideDivDomain &&
          <div className='hoverable-div-parent' style={{ left: '6.8em' }}>
            <p className='hover-content'>
              Only the domain(s) added to this list will be
              activated for alerting and will be shown in the
              dashboards.
            </p>
          </div>
        }
        <h3 style={{ marginBottom: 0 }} className="modal-category">
          Domains
          <InfoCircleFilled
            size={15}
            className='info-icon'
            onMouseEnter={() => setShowGuideDivDomain(true)}
            onMouseLeave={() => setShowGuideDivDomain(false)}
          />
        </h3>
        <p style={{ marginBottom: 0 }}>Add the domain(s) for which you would
          like to activate the Tag Monitor in the list below.</p>
        <div className="list-col-2-add">
          {curCompanyDomains?.map((elem, idx) =>
            <div key={idx} className="inputs-row-3-col-single d-flex flex-row">
              <input type="text" placeholder="code-cube.io" value={elem.domain || ""} onChange={(event) => handleInputValueChange(idx, "domain", event)} />
              <div>
                <DashCircle className='color-blue' size={18} onClick={() => handleElemDelete(idx)}></DashCircle>
              </div>
            </div>
          )
          }
        </div>
        <div>
          <div className="btn btn-sm cc-btn-outline-blue" onClick={handleElemAdd}>+ Add domain</div>
        </div>
      </div>

      <TimezoneDropdown
        choosenTimezone={choosenTimezone}
        timezones={timezones}
        timezoneName={timezoneName}
        handleTimezoneChange={handleTimezoneChange}
      />


    </div>
  );

}

export const CSTSettingsModal = ({ context }) => {
  const {
    databaseName,
    CSImplmenetationLink,
    clientSideTemplateLink,
    clientErrorsThreshold,
    setConfigParams,
    setModalSaveStatus,
    gtmContainers,
    customNmbrCid,
    subscriptionModel,
    tags_monitoring_exclude_client,
  } = useContext(context);
  const navigate = useNavigate();

  const percentages = [1, 5, 10, 25, 50, 100];

  const [curThreshold, setCurThreshold] = useState(clientErrorsThreshold);
  const [configParamsToSave, setConfigParamsToSave] = useState({});
  const [showGuideDiv, setShowGuideDiv] = useState(false);
  const [maxNmbrCids, setMaxNmbrCids] = useState(false);
  const [trigger, setTrigger] = useState("");
  const [showGuideDivGTM, setShowGuideDivGTM] = useState(false);

  const [curGTMContainers, setCurGTMContainers] = useState(
    gtmContainers && gtmContainers.length ? gtmContainers : [{ type: "client-side" }]
  );
  
  const excludetagsby = ["tag name", "tag id"];
  const matchOptions  = ["exactly matches", "matches regex", "contains"];
  const defaultType   = excludetagsby[0];
  const defaultMatch  = matchOptions[0];

  const normalizeTag = (tag) => ({
    type:  tag?.type  || defaultType,
    match: tag?.match || defaultMatch,
    value: tag?.value || "",
  });
  const inputRefDataset = useRef(null);

  const content = (
    <div>
      <p>
        You have reached the maximum number of containers  <br></br>
        allowed by your subscription.  <br></br>
        To add more containers or upgrade your plan, <br></br>
        please contact us
        <a target='blank' href='https://code-cube.io/tag-monitor/upgrade-license/'> here</a>.
      </p>
    </div>
  );

  const [tagsMonitoringExcludeClient, setTagsMonitoringExcludeClient] = useState(
    Array.isArray(tags_monitoring_exclude_client) && tags_monitoring_exclude_client.length > 0
      ? tags_monitoring_exclude_client.map(normalizeTag)
      : [normalizeTag({})]
  );

  useEffect(() => {
    setTagsMonitoringExcludeClient(
      Array.isArray(tags_monitoring_exclude_client) && tags_monitoring_exclude_client.length > 0
        ? tags_monitoring_exclude_client.map(normalizeTag)
        : [normalizeTag({})]
    );
  }, [tags_monitoring_exclude_client]);


  useEffect(() => {
    // Set the maximum number of containers per subscription model
    switch (customNmbrCid > 0) {
      case true:
        setMaxNmbrCids(customNmbrCid);
        break;
      default:
        switch (subscriptionModel) {
          case "essentials":
            setMaxNmbrCids(1);
            break;
          case "advanced":
            setMaxNmbrCids(3);
            break;
          case "premium":
          case "enterprise":
            setMaxNmbrCids(10);
            break;
          default:
            setMaxNmbrCids(1);
        }
    }
  }, [subscriptionModel]);

  useEffect(() => {
    setTrigger(curGTMContainers?.length >= maxNmbrCids ? "hover" : "");
  }, [maxNmbrCids, curGTMContainers]);

  const handleElemDeleteGTM = (idx) => {
    const newGtmContainerList = curGTMContainers.filter((_, index) => index !== idx);
    setCurGTMContainers(newGtmContainerList.length ? newGtmContainerList : [{ type: "client-side" }]);
  };

  const handleElemAddGTM = () => {
    if (curGTMContainers?.length < maxNmbrCids) {
      setCurGTMContainers([...curGTMContainers, { type: "client-side" }]);
    }
  }

  const handleGTMContainerInputValueChange = (idx, event, valueType) => {
    const newGTMContainerList = [...curGTMContainers];
    const elem = { ...newGTMContainerList[idx] };

    switch (valueType) {
      case "cidType":
        elem['type'] = event.target.value;
        break;
      case "gtmID":
        elem['container'] = event.target.value;
        elem['type'] = 'client-side';
        break;
      default:
    }

    newGTMContainerList[idx] = elem;
    setCurGTMContainers(newGTMContainerList);
  }


  const handleElemAddExcludedClientTag = () => {
    setTagsMonitoringExcludeClient(prev => [
      ...prev,
      normalizeTag({}),
    ]);
  };

  const handleElemDeleteExcludedClientTag = (idx) => {
    setTagsMonitoringExcludeClient(prev => {
      const updated = prev.filter((_, i) => i !== idx);
      return updated.length ? updated : [normalizeTag({})];
    });
  };

  const handleExcludedTagInputValue = (idx, field, newValue) => {
     setTagsMonitoringExcludeClient(prev => {
       const updated = [...prev];
       updated[idx] = {
         ...updated[idx],
         [field]: newValue ?? (
           field === "type"  ? defaultType :
           field === "match" ? defaultMatch :
           ""
         ),
       };
       return updated;
     });
   };

  const handleThresholdChange = (elem) => setCurThreshold(elem);

  useEffect(() => setCurThreshold(clientErrorsThreshold), [clientErrorsThreshold]);

  const isGTMContainerEmpty = (containerObj) => {
    return !containerObj.container || containerObj.container.trim() === '';
  };

  const filterEmptyGTMContainers = (containers) =>
    containers.filter(container => !isGTMContainerEmpty(container));

  const isTagExclusionEmpty = (tag) => {
    return !tag.value || tag.value.trim() === '';
  };

  const filterEmptyTagExclusions = (tags) =>
    tags.filter(tag => !isTagExclusionEmpty(tag));


  useEffect(() => {
    // Filter the current and initial GTM container arrays
    const filteredGTMContainers = filterEmptyGTMContainers(curGTMContainers);
    const filteredInitialGTMContainers = filterEmptyGTMContainers(gtmContainers);
    const gtmChanged =
      JSON.stringify(filteredGTMContainers) !== JSON.stringify(filteredInitialGTMContainers);

    // Filter the current and initial tag exclusion arrays
    const filteredTagExclusions = filterEmptyTagExclusions(tagsMonitoringExcludeClient);
    const filteredInitialTagExclusions = filterEmptyTagExclusions(tags_monitoring_exclude_client);
    const tagsChanged =
      JSON.stringify(filteredTagExclusions) !== JSON.stringify(filteredInitialTagExclusions);

    // Check threshold changes (if applicable)
    const thresholdChanged = Number(curThreshold) !== Number(clientErrorsThreshold);

    // Save the filtered values in your config parameters
    setConfigParamsToSave(prevParams => ({
      ...prevParams,
      gtm_containers_client: filteredGTMContainers,
      tags_monitoring_exclude_client: filteredTagExclusions,
      client_errors_threshold: curThreshold,
    }));

    setModalSaveStatus(gtmChanged || tagsChanged || thresholdChanged);
  }, [
    curGTMContainers,
    tagsMonitoringExcludeClient,
    curThreshold,
    gtmContainers,
    tags_monitoring_exclude_client,
    clientErrorsThreshold
  ]);


  useEffect(() => setConfigParams(configParamsToSave), [configParamsToSave]);

  useEffect(() => setModalSaveStatus(Number(curThreshold) !== Number(clientErrorsThreshold)), [curThreshold]);


  return (
    <div className="modal-elems-rows">
      <div>
        <h2 className='header-letter-spacing'>Client-side error monitoring</h2>
      </div>
      <div className="container-guide modal-panel-button-row" style={{ display: 'flex', gap: '1em' }}>
        <Link className="modal-guide-banner blue-bg" style={{ flex: 1 }} target="_blank" to={CSImplmenetationLink}>
          <div className="file-icon">
            <svg width="26" height="31" viewBox="0 0 26 31" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g filter="url(#filter0_d_190_157)">
                <path d="M2.64922 3.41406H16.4948V7.60147C16.4948 7.95999 16.7855 8.25062 17.144 8.25062H19.3979V26.6164C19.3979 26.975 19.1073 27.2656 18.7488 27.2656H2.64922C2.2907 27.2656 2.00006 26.975 2.00006 26.6164V4.06322C2.00006 3.7047 2.2907 3.41406 2.64922 3.41406Z" fill="#69C4FF" fillOpacity="0.5" />
                <path d="M3.72516 24.6433H16.4543C16.4543 24.8469 16.6193 25.0119 16.8228 25.0119H19.4347V25.8118C19.4347 26.1703 19.1441 26.4609 18.7856 26.4609H4.37431C4.0158 26.4609 3.72516 26.1703 3.72516 25.8118V24.6433Z" fill="#69C4FF" fillOpacity="0.5" />
                <path d="M4.34194 1.79309H18.3155V5.98808C18.3155 6.3466 18.6061 6.63724 18.9646 6.63724H21.7393V25.0329C21.7393 25.3914 21.4487 25.682 21.0902 25.682H4.34194C3.98342 25.682 3.69278 25.3914 3.69278 25.0329V2.44225C3.69278 2.08373 3.98342 1.79309 4.34194 1.79309Z" fill="#DBF1FF" />
                <path d="M5.41306 22.566H18.6682C18.6682 22.755 18.8214 22.9082 19.0104 22.9082H21.7718V23.6046C21.7718 23.9631 21.4811 24.2538 21.1226 24.2538H6.06221C5.70369 24.2538 5.41306 23.9631 5.41306 23.6046V22.566Z" fill="#B0E0FF" />
                <path d="M6.06221 0.170166H18.7207V4.25985C18.7207 4.61837 19.0114 4.909 19.3699 4.909H23.46V22.6673C23.46 23.1492 23.0694 23.5398 22.5875 23.5398H6.06221C5.70369 23.5398 5.41306 23.2491 5.41306 22.8906V0.819322C5.41306 0.460803 5.70369 0.170166 6.06221 0.170166Z" fill="white" />
                <line x1="8.82252" y1="9.40248" x2="19.988" y2="9.40248" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                <line x1="8.90306" y1="15.1451" x2="20.0685" y2="15.1451" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                <line x1="8.82252" y1="11.3499" x2="19.988" y2="11.3499" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                <line x1="8.90306" y1="17.0925" x2="20.0685" y2="17.0925" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                <line x1="8.82252" y1="13.1675" x2="19.988" y2="13.1675" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                <path d="M19.3703 4.946C19.0118 4.94601 18.7212 4.65537 18.7212 4.29685L18.7212 0.170195L23.4599 4.94597L19.3703 4.946Z" fill="#69C4FF" fillOpacity="0.14" />
              </g>
              <defs>
                <filter id="filter0_d_190_157" x="0.255097" y="0.170166" width="24.9498" height="30.5854" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                  <feOffset dy="1.74496" />
                  <feGaussianBlur stdDeviation="0.872482" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix type="matrix" values="0 0 0 0 0.168627 0 0 0 0 0.168627 0 0 0 0 0.709804 0 0 0 0.15 0" />
                  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_190_157" />
                  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_190_157" result="shape" />
                </filter>
              </defs>
            </svg>


          </div>
          <div className="panel-button-text">
            <div className="panel-button-desc">Guide how to set up in Tag Manager</div>
          </div>
        </Link>
        <Link className="modal-guide-banner blue-bg" style={{ flex: 1 }} target="_blank" to={clientSideTemplateLink}>
          <div className="file-icon">
            <svg width="31" height="31" viewBox="0 0 31 31" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g filter="url(#filter0_d_141_70)">
                <path d="M5.17672 7.1814H8.42156C9.13839 7.1814 9.71949 7.7625 9.71949 8.47933V19.9444C9.71949 20.6613 9.13839 21.2424 8.42156 21.2424H5.17672C4.45989 21.2424 3.87878 20.6613 3.87878 19.9444V8.47933C3.87878 7.7625 4.45989 7.1814 5.17672 7.1814Z" fill="#D8F0FF" stroke="#B0E0FF" strokeWidth="0.648968" />
                <line x1="9.58228" y1="14.3395" x2="19.3946" y2="14.3395" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.342291" strokeLinecap="round" />
                <line x1="9.51154" y1="11.0043" x2="19.3239" y2="11.0043" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.342291" strokeLinecap="round" />
                <line x1="9.58228" y1="16.0509" x2="19.3946" y2="16.0509" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.342291" strokeLinecap="round" />
                <line x1="9.51154" y1="12.6017" x2="19.3239" y2="12.6017" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.342291" strokeLinecap="round" />
                <path d="M8.62264 0.229834L19.6322 1.8242L19.1171 5.38115C19.072 5.69297 19.2882 5.98235 19.6 6.02751L23.1573 6.54266L20.9206 21.9877C20.8599 22.4068 20.4709 22.6973 20.0519 22.6366L5.6792 20.5553C5.36738 20.5101 5.15121 20.2207 5.19636 19.9089L7.97628 0.712666C8.02144 0.400849 8.31082 0.184678 8.62264 0.229834Z" fill="white" />
                <line x1="9.86059" y1="8.60723" x2="19.5716" y2="10.0135" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.342291" strokeLinecap="round" />
                <path d="M19.5957 6.05963C19.2839 6.01448 19.0677 5.72509 19.1129 5.41327L19.6327 1.82417L23.1526 6.5747L19.5957 6.05963Z" fill="#69C4FF" fillOpacity="0.14" />
                <path d="M25.2865 10.6967H8.13698C7.36822 10.6967 6.68867 11.1963 6.45937 11.93L3.71309 20.7181C3.3594 21.8499 4.20494 23 5.3907 23H22.8149C23.6023 23 24.2937 22.4763 24.5069 21.7182L25.8765 16.8483L26.9785 12.9301C27.2939 11.8089 26.4512 10.6967 25.2865 10.6967Z" fill="#B0E0FF" />
              </g>
              <defs>
                <filter id="filter0_d_141_70" x="0.0390809" y="0.223877" width="30.5213" height="29.8066" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                  <feOffset dy="3.51524" />
                  <feGaussianBlur stdDeviation="1.75762" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
                  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_141_70" />
                  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_141_70" result="shape" />
                </filter>
              </defs>
            </svg>

          </div>
          <div className="panel-button-text">
            <div className="panel-button-desc">For GTM client-side container</div>
          </div>
        </Link>
      </div>
      <div style={{ position: 'relative' }} className="elems-list">
        <h3 className="modal-category" style={{ display: 'flex', alignItems: 'center' }}>
          Container set-up
          <InfoCircleFilled
            size={15}
            className='info-icon'
            style={{ marginLeft: '10px' }}
          />
        </h3>
        <p style={{ lineHeight: '20px', marginBottom: '15px' }}>
          Add your Google Tag Manager container ID'(s) to the list below.
        </p>
        {
          showGuideDivGTM &&
          <div className='hoverable-div-parent' style={{ left: '15em' }}>
            <p className='hover-content'>
              Only the container(s) added to this list will be
              activated for alerting and will show up in the
              dashboards.
            </p>
          </div>
        }
        <div className="list-col-2-add">
          {curGTMContainers?.map((elem, idx) =>
            <div key={idx} className="inputs-row-3-col-single">
              <input type="text" placeholder="GTM-XXXXX" value={elem?.container || ""} onChange={(event) => handleGTMContainerInputValueChange(idx, event, 'gtmID')} />
              <div style={{ display: 'flex', gap: '10px' }}>
                <DashCircle
                  className='color-blue'
                  size={18}
                  onClick={() => handleElemDeleteGTM(idx)}
                  style={{ cursor: "pointer" }}
                />
                <Popover content={content} title="Container Limit Reached!" trigger={trigger}>
                  {idx === curGTMContainers.length - 1 ? (
                    <PlusCircle
                      className='color-blue'
                      size={18}
                      style={{ cursor: "pointer" }}
                      onClick={() => handleElemAddGTM(idx)}
                    />
                  ) : null}
                </Popover>
              </div>
            </div>
          )}
        </div>
      </div>

      <div>
        <h3 className="modal-category" style={{ display: 'flex', alignItems: 'center' }}>
          Installation
        </h3>
        <div style={{ padding: '20px' }} >
          <ol style={{ paddingLeft: '0', margin: '0', textAlign: 'left' }} >
            <li>Download the Code Cube template via the button above.</li>
            <li>Import the template in your GTM container and create a new monitoring tag.</li>
            <li>Copy the "Dataset name" the Code Cube monitoring tag.</li>
            <li>Add <strong>"raw_data_client"</strong> as value in the field "Table ID".</li>
          </ol>
        </div>
        <div className='d-flex flex-column' style={{ gap: '.5em' }}>
          <div>
            <div>
              <input
                className='disabled-input'
                type="text"
                placeholder="Database name"
                value={databaseName || ""}
                disabled
                ref={inputRefDataset}
              />
              <Copy
                className='copy-icon'
                size={21}
                onClick={() => copyToClipboard(inputRefDataset)}
              />
            </div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="modal-category" style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
          Configuration
        </h3>
        <p className="modal-category fw-bold" style={{ lineHeight: '20px', marginBottom: '15px' }}>
          Configure tag names
        </p>
        <p style={{ lineHeight: '20px', marginBottom: '15px' }}>
          Tag Monitor shows Tag ID'S by default. Add friendly names to your tags.
          Click "Start bulk editing" to begin.
        </p>
        <div
          className="btn btn-sm cc-btn-outline-blue"
          style={{ marginBottom: '30px', cursor: 'pointer' }}
          onClick={() => navigate('/authorize_user')}
        >
          Start bulk editing
        </div>
      </div>

      <div style={{ position: 'relative' }}>
        <p className="modal-category fw-bold" style={{ lineHeight: '20px', marginBottom: '15px' }}>
          Alert threshold
          <InfoCircleFilled
            size={15}
            className='info-icon'
            style={{ marginLeft: '10px' }}
          />
        </p>
        <p style={{ lineHeight: '20px', marginBottom: '15px' }}>
          Choose the error percentage threshold to determine when you should receive notifications.
        </p>
        <div style={{ marginTop: '15px' }}>
          <Select
            style={{ width: '8em' }}
            value={`${curThreshold}%`}
            popupClassName="custom-dropdown"
            onChange={(value) => handleThresholdChange(parseInt(value))}
          >
            {percentages.map((elem) => (
              <Option key={elem} value={`${elem}`}>{`${elem}%`}</Option>
            ))}
          </Select>
        </div>
      </div>

      <div style={{ position: 'relative' }}>
        <p className="modal-category fw-bold" style={{ lineHeight: '20px', marginBottom: '15px' }}>
          Tags to exclude from monitoring
        </p>
        <p style={{ lineHeight: '20px', marginBottom: '15px' }}>
          Select tags to exclude from monitoring. Add exclusions directly in GTM or use the table below.
        </p>
        <div className='list-col-2-add'>
          {Array.isArray(tagsMonitoringExcludeClient) && tagsMonitoringExcludeClient.map((elem, idx) =>
            <div key={idx} className="inputs-row-3-col-single d-flex flex-row" style={{ padding: 0 }}>
              <div className='dropdown'>
                <Select
                  style={{ minWidth: "9rem" }}
                  showSearch
                  value={elem?.type || "tag name"}
                    onChange={(value) => handleExcludedTagInputValue(idx, "type", value)}
                  popupClassName="custom-dropdown"
                >
                  {excludetagsby.map(option => (
                    <Select.Option key={option} value={option}>
                      {option}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              <div>
                <Select
                  value={elem?.match || "exactly matches"}
                  style={{ minWidth: "9rem" }}
                  showSearch
                  onChange={(value) => handleExcludedTagInputValue(idx, "match", value)}
                  popupClassName="custom-dropdown"
                >
                  {matchOptions.map(option => (
                    <Select.Option key={`${option}-${idx}`} value={option}>
                      {option}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              <div className="tags-container" >
                <Input
                  placeholder={elem?.type === "tag id" ? "tag id" : "tag name"}
                  value={elem?.value || ""}
                  onChange={event => handleExcludedTagInputValue(idx, "value", event.target.value)}
                  style={{ minWidth: "14rem" }}
                />
              </div>
              <div>
                <DashCircle
                  className='color-blue'
                  size={18}
                  onClick={() => handleElemDeleteExcludedClientTag(idx)}
                >
                </DashCircle>{" "}
                {idx === tagsMonitoringExcludeClient.length - 1 ? (
                  <PlusCircle
                    className='color-blue'
                    size={18}
                    onClick={handleElemAddExcludedClientTag}
                  >
                  </PlusCircle>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}


export const SSTSettingsModal = ({ context }) => {
  const {
    databaseName,
    SSImplmenetationLink,
    serverSideTemplateLink,
    serverErrorsThreshold,
    setConfigParams,
    setModalSaveStatus,
    gtmContainers,
    serverSideAvailable,
    customNmbrCid,
    subscriptionModel,
    tags_monitoring_exclude_server,
  } = useContext(context);

  const navigate = useNavigate();
  const percentages = [1, 5, 10, 25, 50, 100];

  const [curThreshold, setCurThreshold] = useState(serverErrorsThreshold);
  const [configParamsToSave, setConfigParamsToSave] = useState({});
  const [showGuideDiv, setShowGuideDiv] = useState(false);
  const [maxNmbrCids, setMaxNmbrCids] = useState(false);
  const [trigger, setTrigger] = useState("");
  const [showGuideDivGTM, setShowGuideDivGTM] = useState(false);

  const [curGTMContainers, setCurGTMContainers] = useState(
    gtmContainers && gtmContainers.length ? gtmContainers : [{ type: "server-side" }]);

  const excludetagsby = ["tag name", "tag id"];
  const matchOptions  = ["exactly matches", "matches regex", "contains"];
  const defaultType   = excludetagsby[0];
  const defaultMatch  = matchOptions[0];

  const normalizeTag = (tag) => ({
    type:  tag?.type  || defaultType,
    match: tag?.match || defaultMatch,
    value: tag?.value || "",
  });

  const [tagsMonitoringExcludeServer, setTagsMonitoringExcludeServer] = useState(
    Array.isArray(tags_monitoring_exclude_server) && tags_monitoring_exclude_server.length > 0
      ? tags_monitoring_exclude_server.map(normalizeTag)
      : [normalizeTag({})]
  );

  useEffect(() => {
  setTagsMonitoringExcludeServer(
    Array.isArray(tags_monitoring_exclude_server) && tags_monitoring_exclude_server.length > 0
      ? tags_monitoring_exclude_server.map(normalizeTag)
      : [normalizeTag({})]
  );
}, [tags_monitoring_exclude_server]);

  const inputRefDataset = useRef(null);

  const content = (
    <div>
      <p>
        You have reached the maximum number of containers  <br></br>
        allowed by your subscription.  <br></br>
        To add more containers or upgrade your plan, <br></br>
        please contact us
        <a target='blank' href='https://code-cube.io/tag-monitor/upgrade-license/'> here</a>.
      </p>
    </div>
  );

  useEffect(() => {
    setTagsMonitoringExcludeServer(
      tags_monitoring_exclude_server && tags_monitoring_exclude_server.length > 0
        ? tags_monitoring_exclude_server
        : [normalizeTag({})]  
    );
  }, [tags_monitoring_exclude_server]);


  useEffect(() => {
    // Set the maximum number of containers per subscription model
    switch (customNmbrCid > 0) {
      case true:
        setMaxNmbrCids(customNmbrCid);
        break;
      default:
        switch (subscriptionModel) {
          case "essentials":
            setMaxNmbrCids(1);
            break;
          case "advanced":
            setMaxNmbrCids(3);
            break;
          case "premium":
          case "enterprise":
            setMaxNmbrCids(10);
            break;
          default:
            setMaxNmbrCids(1);
        }
    }
  }, [subscriptionModel]);


  useEffect(() => {
    setTrigger(curGTMContainers?.length >= maxNmbrCids ? "hover" : "");
  }, [maxNmbrCids, curGTMContainers]);


  const handleElemDeleteGTM = (idx) => {
    const newGtmContainerList = curGTMContainers.filter((_, index) => index !== idx);
    setCurGTMContainers(newGtmContainerList.length ? newGtmContainerList : [{ type: "server-side" }]);
  }
  const handleElemAddGTM = () => {
    if (curGTMContainers?.length < maxNmbrCids) {
      setCurGTMContainers([...curGTMContainers, { type: "server-side" }]);
    }
  }
  const handleGTMContainerInputValueChange = (idx, event, valueType) => {
    const newGTMContainerList = [...curGTMContainers];
    const elem = { ...newGTMContainerList[idx] };

    switch (valueType) {
      case "cidType":
        elem['type'] = event.target.innerText;
        break;
      case "gtmID":
        elem['container'] = event.target.value;
        elem['type'] = 'server-side';
        break;
      default:
    }

    newGTMContainerList[idx] = elem;
    setCurGTMContainers(newGTMContainerList);
  }

  const handleElemAddExcludedServerTag = () => {
    setTagsMonitoringExcludeServer(prev => [
      ...prev,
      normalizeTag({}),
    ]);
  };

  const handleElemDeleteExcludedServerTag = (idx) => {
    setTagsMonitoringExcludeServer(prev => {
      const next = prev.filter((_, i) => i !== idx);
      return next.length ? next : [normalizeTag({})];
    });
  };

  const handleExcludedTagInputValue = (idx, field, newValue) => {
    const newExcludedElemList = [...tagsMonitoringExcludeServer];
    const elem = { ...newExcludedElemList[idx] };

    switch (field) {
      case "type":
        elem.type = newValue === "tag name" ? "tag name" : "tag id";
        break;
      case "match":
        elem.match = newValue;
        break;
      case "value":
        elem.value = newValue;
        break;
      default:
        break;
    }

    newExcludedElemList[idx] = elem;
    setTagsMonitoringExcludeServer(newExcludedElemList);
  };

  const handleThresholdChange = (elem) => setCurThreshold(elem);

  useEffect(() => setCurThreshold(serverErrorsThreshold), [serverErrorsThreshold]);

  const isGTMContainerEmpty = (containerObj) => {
    return !containerObj.container || containerObj.container.trim() === '';
  };

  const filterEmptyGTMContainers = (containers) =>
    containers.filter(container => !isGTMContainerEmpty(container));

  const isTagExclusionEmpty = (tag) => {
    return !tag.value || tag.value.trim() === '';
  };

  const filterEmptyTagExclusions = (tags) =>
    tags.filter(tag => !isTagExclusionEmpty(tag));


  useEffect(() => {
    // Filter the current and initial GTM container arrays
    const filteredGTMContainers = filterEmptyGTMContainers(curGTMContainers);
    const filteredInitialGTMContainers = filterEmptyGTMContainers(gtmContainers);
    const gtmChanged =
      JSON.stringify(filteredGTMContainers) !== JSON.stringify(filteredInitialGTMContainers);

    // Filter the current and initial tag exclusion arrays
    const filteredTagExclusions = filterEmptyTagExclusions(tagsMonitoringExcludeServer);
    const filteredInitialTagExclusions = filterEmptyTagExclusions(tags_monitoring_exclude_server);
    const tagsChanged =
      JSON.stringify(filteredTagExclusions) !== JSON.stringify(filteredInitialTagExclusions);

    // Check threshold changes (if applicable)
    const thresholdChanged = Number(curThreshold) !== Number(serverErrorsThreshold);

    // Save the filtered values in your config parameters
    setConfigParamsToSave(prevParams => ({
      ...prevParams,
      gtm_containers_server: filteredGTMContainers,
      tags_monitoring_exclude_server: filteredTagExclusions,
      client_errors_threshold: curThreshold,
    }));

    setModalSaveStatus(gtmChanged || tagsChanged || thresholdChanged);
  }, [
    curGTMContainers,
    tagsMonitoringExcludeServer,
    curThreshold,
    gtmContainers,
    tags_monitoring_exclude_server,
    serverErrorsThreshold
  ]);


  useEffect(() => setConfigParams(configParamsToSave), [configParamsToSave]);

  useEffect(() => setModalSaveStatus(Number(curThreshold) !== Number(serverErrorsThreshold)), [curThreshold]);


  return (
    <div className="modal-elems-rows">
      <div>
        <h2 className='header-letter-spacing' >Server-side error monitoring</h2>
      </div>
      <div className="container-guide modal-panel-button-row" style={{ display: 'flex', gap: '1em' }}>
        <Link className="modal-guide-banner blue-bg" style={{ flex: 1 }} target="_blank" to={SSImplmenetationLink}>
          <div className="file-icon">
            <svg width="26" height="31" viewBox="0 0 26 31" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g filter="url(#filter0_d_190_157)">
                <path d="M2.64922 3.41406H16.4948V7.60147C16.4948 7.95999 16.7855 8.25062 17.144 8.25062H19.3979V26.6164C19.3979 26.975 19.1073 27.2656 18.7488 27.2656H2.64922C2.2907 27.2656 2.00006 26.975 2.00006 26.6164V4.06322C2.00006 3.7047 2.2907 3.41406 2.64922 3.41406Z" fill="#69C4FF" fillOpacity="0.5" />
                <path d="M3.72516 24.6433H16.4543C16.4543 24.8469 16.6193 25.0119 16.8228 25.0119H19.4347V25.8118C19.4347 26.1703 19.1441 26.4609 18.7856 26.4609H4.37431C4.0158 26.4609 3.72516 26.1703 3.72516 25.8118V24.6433Z" fill="#69C4FF" fillOpacity="0.5" />
                <path d="M4.34194 1.79309H18.3155V5.98808C18.3155 6.3466 18.6061 6.63724 18.9646 6.63724H21.7393V25.0329C21.7393 25.3914 21.4487 25.682 21.0902 25.682H4.34194C3.98342 25.682 3.69278 25.3914 3.69278 25.0329V2.44225C3.69278 2.08373 3.98342 1.79309 4.34194 1.79309Z" fill="#DBF1FF" />
                <path d="M5.41306 22.566H18.6682C18.6682 22.755 18.8214 22.9082 19.0104 22.9082H21.7718V23.6046C21.7718 23.9631 21.4811 24.2538 21.1226 24.2538H6.06221C5.70369 24.2538 5.41306 23.9631 5.41306 23.6046V22.566Z" fill="#B0E0FF" />
                <path d="M6.06221 0.170166H18.7207V4.25985C18.7207 4.61837 19.0114 4.909 19.3699 4.909H23.46V22.6673C23.46 23.1492 23.0694 23.5398 22.5875 23.5398H6.06221C5.70369 23.5398 5.41306 23.2491 5.41306 22.8906V0.819322C5.41306 0.460803 5.70369 0.170166 6.06221 0.170166Z" fill="white" />
                <line x1="8.82252" y1="9.40248" x2="19.988" y2="9.40248" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                <line x1="8.90306" y1="15.1451" x2="20.0685" y2="15.1451" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                <line x1="8.82252" y1="11.3499" x2="19.988" y2="11.3499" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                <line x1="8.90306" y1="17.0925" x2="20.0685" y2="17.0925" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                <line x1="8.82252" y1="13.1675" x2="19.988" y2="13.1675" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                <path d="M19.3703 4.946C19.0118 4.94601 18.7212 4.65537 18.7212 4.29685L18.7212 0.170195L23.4599 4.94597L19.3703 4.946Z" fill="#69C4FF" fillOpacity="0.14" />
              </g>
              <defs>
                <filter id="filter0_d_190_157" x="0.255097" y="0.170166" width="24.9498" height="30.5854" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                  <feOffset dy="1.74496" />
                  <feGaussianBlur stdDeviation="0.872482" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix type="matrix" values="0 0 0 0 0.168627 0 0 0 0 0.168627 0 0 0 0 0.709804 0 0 0 0.15 0" />
                  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_190_157" />
                  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_190_157" result="shape" />
                </filter>
              </defs>
            </svg>

          </div>
          <div className="panel-button-text">
            <div className="panel-button-desc">Guide how to set up in Tag Manager</div>
          </div>
        </Link>
        <Link className="modal-guide-banner blue-bg" style={{ flex: 1 }} target="_blank" to={serverSideTemplateLink}>
          <div className="file-icon">
            <svg width="31" height="31" viewBox="0 0 31 31" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g filter="url(#filter0_d_141_70)">
                <path d="M5.17672 7.1814H8.42156C9.13839 7.1814 9.71949 7.7625 9.71949 8.47933V19.9444C9.71949 20.6613 9.13839 21.2424 8.42156 21.2424H5.17672C4.45989 21.2424 3.87878 20.6613 3.87878 19.9444V8.47933C3.87878 7.7625 4.45989 7.1814 5.17672 7.1814Z" fill="#D8F0FF" stroke="#B0E0FF" strokeWidth="0.648968" />
                <line x1="9.58228" y1="14.3395" x2="19.3946" y2="14.3395" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.342291" strokeLinecap="round" />
                <line x1="9.51154" y1="11.0043" x2="19.3239" y2="11.0043" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.342291" strokeLinecap="round" />
                <line x1="9.58228" y1="16.0509" x2="19.3946" y2="16.0509" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.342291" strokeLinecap="round" />
                <line x1="9.51154" y1="12.6017" x2="19.3239" y2="12.6017" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.342291" strokeLinecap="round" />
                <path d="M8.62264 0.229834L19.6322 1.8242L19.1171 5.38115C19.072 5.69297 19.2882 5.98235 19.6 6.02751L23.1573 6.54266L20.9206 21.9877C20.8599 22.4068 20.4709 22.6973 20.0519 22.6366L5.6792 20.5553C5.36738 20.5101 5.15121 20.2207 5.19636 19.9089L7.97628 0.712666C8.02144 0.400849 8.31082 0.184678 8.62264 0.229834Z" fill="white" />
                <line x1="9.86059" y1="8.60723" x2="19.5716" y2="10.0135" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.342291" strokeLinecap="round" />
                <path d="M19.5957 6.05963C19.2839 6.01448 19.0677 5.72509 19.1129 5.41327L19.6327 1.82417L23.1526 6.5747L19.5957 6.05963Z" fill="#69C4FF" fillOpacity="0.14" />
                <path d="M25.2865 10.6967H8.13698C7.36822 10.6967 6.68867 11.1963 6.45937 11.93L3.71309 20.7181C3.3594 21.8499 4.20494 23 5.3907 23H22.8149C23.6023 23 24.2937 22.4763 24.5069 21.7182L25.8765 16.8483L26.9785 12.9301C27.2939 11.8089 26.4512 10.6967 25.2865 10.6967Z" fill="#B0E0FF" />
              </g>
              <defs>
                <filter id="filter0_d_141_70" x="0.0390809" y="0.223877" width="30.5213" height="29.8066" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                  <feOffset dy="3.51524" />
                  <feGaussianBlur stdDeviation="1.75762" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
                  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_141_70" />
                  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_141_70" result="shape" />
                </filter>
              </defs>
            </svg>

          </div>
          <div className="panel-button-text">
            <div className="panel-button-desc">For GTM server-side container</div>
          </div>
        </Link>
      </div>
      <div style={{ position: 'relative' }} className="elems-list">
        <h3 className="modal-category" style={{ display: 'flex', alignItems: 'center' }}>
          Container set-up
          <InfoCircleFilled
            size={15}
            className='info-icon'
            style={{ marginLeft: '10px' }}
          />
        </h3>
        <p style={{ lineHeight: '20px', marginBottom: '15px' }}>
          Add your Google Tag Manager container ID'(s) to the list below.
        </p>
        {
          showGuideDivGTM &&
          <div className='hoverable-div-parent' style={{ left: '15em' }}>
            <p className='hover-content'>
              Only the container(s) added to this list will be
              activated for alerting and will show up in the
              dashboards.
            </p>
          </div>
        }
        <div className="list-col-2-add">
          {curGTMContainers?.map((elem, idx) =>
            <div key={idx} className="inputs-row-3-col-single">
              <input type="text" placeholder="GTM-XXXXX" value={elem?.container || ""} onChange={(event) => handleGTMContainerInputValueChange(idx, event, 'gtmID')} />
              <div style={{ display: 'flex', gap: '10px' }}>
                <DashCircle
                  className='color-blue'
                  size={18}
                  onClick={() => handleElemDeleteGTM(idx)}
                  style={{ cursor: "pointer" }}
                />
                <Popover content={content} title="Container Limit Reached!" trigger={trigger}>
                  {idx === curGTMContainers.length - 1 ? (
                    <PlusCircle
                      className='color-blue'
                      size={18}
                      style={{ cursor: "pointer" }}
                      onClick={() => handleElemAddGTM(idx)}
                    />
                  ) : null}
                </Popover>
              </div>
            </div>
          )}
        </div>
      </div>

      <div>
        <h3 className="modal-category" style={{ display: 'flex', alignItems: 'center' }}>
          Installation
        </h3>
        <div style={{ padding: '20px' }} >
          <ol style={{ paddingLeft: '0', margin: '0', textAlign: 'left' }} >
            <li>Download the Code Cube template via the button above.</li>
            <li>Import the template in your GTM container and create a new monitoring tag.</li>
            <li>Copy the "Dataset name" the Code Cube monitoring tag.</li>
            <li>Add <strong>"raw_data_server"</strong> as value in the field "Table ID".</li>
          </ol>
        </div>
        <div className='d-flex flex-column' style={{ gap: '.5em' }}>
          <div>
            <div>
              <input
                className='disabled-input'
                type="text"
                placeholder="Database name"
                value={databaseName || ""}
                disabled
                ref={inputRefDataset}
              />
              <Copy
                className='copy-icon'
                size={21}
                onClick={() => copyToClipboard(inputRefDataset)}
              />
            </div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="modal-category" style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
          Configuration
        </h3>
        <p className="modal-category fw-bold" style={{ lineHeight: '20px', marginBottom: '15px' }}>
          Configure tag names
        </p>
        <p style={{ lineHeight: '20px', marginBottom: '15px' }}>
          Tag Monitor shows Tag ID'S by default. Add friendly names to your tags.
          Click "Start bulk editing" to begin.
        </p>
        <div
          className="btn btn-sm cc-btn-outline-blue"
          style={{ marginBottom: '30px', cursor: 'pointer' }}
          onClick={() => navigate('/authorize_user')}
        >
          Start bulk editing
        </div>
      </div>

      <div style={{ position: 'relative' }}>
        <p className="modal-category fw-bold" style={{ lineHeight: '20px', marginBottom: '15px' }}>
          Alert threshold
          <InfoCircleFilled
            size={15}
            className='info-icon'
            style={{ marginLeft: '10px' }}
          />
        </p>
        {
          showGuideDiv &&
          <div className='hoverable-div-parent' style={{ left: '10em' }}>
            <p className='hover-content'>
              Notifications via email, Slack, or Teams are triggered only
              when the error threshold is met
            </p>
          </div>
        }
        <p style={{ lineHeight: '20px', marginBottom: '15px' }}>
          Choose the error percentage threshold to determine when you should receive notifications.
        </p>
        <div style={{ marginTop: '15px' }}>
          <Select
            style={{ width: '8em' }}
            value={`${curThreshold}%`}
            popupClassName="custom-dropdown"
            onChange={(value) => handleThresholdChange(parseInt(value))}
          >
            {percentages.map((elem) => (
              <Option key={elem} value={`${elem}`}>{`${elem}%`}</Option>
            ))}
          </Select>
        </div>
      </div>

      <div style={{ position: 'relative' }}>
        <p className="modal-category fw-bold" style={{ lineHeight: '20px', marginBottom: '15px' }}>
          Tags to exclude from monitoring
        </p>
        <p style={{ lineHeight: '20px', marginBottom: '15px' }}>
          Select tags to exclude from monitoring. Add exclusions directly in GTM or use the table below.
        </p>
        <div className='list-col-2-add'>
          {Array.isArray(tagsMonitoringExcludeServer) && tagsMonitoringExcludeServer.map((elem, idx) =>
            <div key={idx} className="inputs-row-3-col-single d-flex flex-row" style={{ padding: 0 }}>
              <div className='dropdown'>
                <Select
                  style={{ minWidth: "9rem" }}
                  showSearch
                  value={elem?.type || "tag name"}
                  onChange={(value) => handleExcludedTagInputValue(idx, "type", value)}
                  popupClassName="custom-dropdown"
                >
                  {excludetagsby.map(option => (
                    <Select.Option key={option} value={option}>
                      {option}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              <div>
                <Select
                  value={elem?.match || "exactly matches"}
                  style={{ minWidth: "9rem" }}
                  showSearch
                 onChange={(value) => handleExcludedTagInputValue(idx, "match", value)}
                  popupClassName="custom-dropdown"
                >
                  {matchOptions.map(option => (
                    <Select.Option key={`${option}-${idx}`} value={option}>
                      {option}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              <div className="tags-container" >
                <Input
                  placeholder={elem?.type === "tag id" ? "tag id" : "tag name"}
                  value={elem?.value || ""}
                  onChange={event => handleExcludedTagInputValue(idx, "value", event.target.value)}
                  style={{ minWidth: "14rem" }}
                />
              </div>
              <div>
                <DashCircle
                  className='color-blue'
                  size={18}
                  onClick={() => handleElemDeleteExcludedServerTag(idx)}
                >
                </DashCircle>{" "}
                {idx === tagsMonitoringExcludeServer.length - 1 ? (
                  <PlusCircle
                    className='color-blue'
                    size={18}
                    onClick={handleElemAddExcludedServerTag}
                  >
                  </PlusCircle>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export const TagInactivitySettingsModal = ({ context, onClose }) => {
  const {
    TIImplmenetationLink,
    gtmContainers,
    companyDomains,
    checkFrequency,
    tagsInactivityClientSections,
    tagsInactivityServerSections,
    setConfigParams,
    setModalSaveStatus,
  } = useContext(context);

  // 1. Initialize with an empty default rule
  const defaultEmptyRule = {
    id: 1,
    sectionTitle: "# Default monitoring rule",
    isActive: true,
    containerIDs: [],
    domains: [],
    tagDetails: [{ tagType: "tag name", matchType: "exactly matches", value: "" }],
    trigger: [{ type: "hostname", matchType: "exactly matches", value: "" }],
    frequency: checkFrequency || "hourly",
  };

  const [configSections, setConfigSections] = useState([defaultEmptyRule]);
  const [configParamsToSave, setConfigParamsToSave] = useState({});
  const [curCompanyDomains, setCurCompanyDomains] = useState(companyDomains || []);
  const [showGuideDiv, setShowGuideDiv] = useState(false);
  const [showGuideDivGTM, setShowGuideDivGTM] = useState(false);
  const [showGuideDivTD, setShowGuideDivTD] = useState(false);
  const [showGuideDivTrigger, setShowGuideDivTrigger] = useState(false);
  const [showGuideDivFrequency, setShowGuideDivFrequency] = useState(false);
  const [isInitialEmpty, setIsInitialEmpty] = useState(false);

  const originalConfigSectionsRef = useRef([]);
  const initialConfigSectionsRef = useRef([]);
  const initialCompanyDomainsRef = useRef(companyDomains || []);
  const nextSectionIdRef = useRef(2);

  useEffect(() => {
    setCurCompanyDomains(companyDomains || []);
  }, [companyDomains]);

  useEffect(() => {
    const mergedSections = [
      ...tagsInactivityClientSections.map((section) => ({
        ...section,
        containerIDs:
          section.containerIDs?.map((c) => ({
            ...c,
            type: c.type || "client-side",
          })) ?? [],
      })),
      ...tagsInactivityServerSections.map((section) => ({
        ...section,
        containerIDs:
          section.containerIDs?.map((c) => ({
            ...c,
            type: c.type || "server-side",
          })) ?? [],
      })),
    ];

    if (mergedSections.length > 0) {
      setConfigSections(mergedSections);
      originalConfigSectionsRef.current = mergedSections;
      initialConfigSectionsRef.current = mergedSections;
      initialCompanyDomainsRef.current = companyDomains || [];
      const maxId = mergedSections.reduce((max, s) => (s.id > max ? s.id : max), 0);
      if (maxId) nextSectionIdRef.current = maxId + 1;
      setIsInitialEmpty(false);
    } else {
      setConfigSections([defaultEmptyRule]);
      originalConfigSectionsRef.current = [defaultEmptyRule];
      initialConfigSectionsRef.current = [defaultEmptyRule];
      initialCompanyDomainsRef.current = companyDomains || [];
      setIsInitialEmpty(true);
    }
  }, [
    gtmContainers,
    companyDomains,
    checkFrequency,
    tagsInactivityClientSections,
    tagsInactivityServerSections,
  ]);

  useEffect(() => {
    if (isInitialEmpty) {
      const hasChanges = configSections.some(section =>
        section.sectionTitle !== defaultEmptyRule.sectionTitle ||
        !_.isEmpty(section.containerIDs) ||
        !_.isEmpty(section.domains) ||
        section.tagDetails.some(tag => tag.value.trim() !== "") ||
        section.trigger.some(trig => trig.value.trim() !== "") ||
        section.frequency !== defaultEmptyRule.frequency
      );
      const domainsChanged = !_.isEqual(initialCompanyDomainsRef.current, curCompanyDomains);
      setModalSaveStatus(hasChanges || domainsChanged);
    } else {
      const sectionsChanged = !_.isEqual(initialConfigSectionsRef.current, configSections);
      const domainsChanged = !_.isEqual(initialCompanyDomainsRef.current, curCompanyDomains);
      setModalSaveStatus(sectionsChanged || domainsChanged);
    }
  }, [configSections, curCompanyDomains, setModalSaveStatus, isInitialEmpty]);

  useEffect(() => {
    const newSections = configSections.map((section) => ({
      sectionTitle: section.sectionTitle,
      isActive: section.isActive,
      containerIDs: section.containerIDs.map((c) => ({
        value: c.value,
        label: c.label,
        type: c.type,
      })),
      domains: section.domains,
      tagDetails: section.tagDetails.map((d) => ({
        tagType: d.tagType,
        matchType: d.matchType,
        value: d.value,
      })),
      trigger: section.trigger.map((t) => ({
        type: t.type,
        matchType: t.matchType,
        value: t.value,
      })),
      frequency: section.frequency,
    }));

    const clientSections = newSections
      .map((sec) => ({
        ...sec,
        containerIDs: sec.containerIDs.filter((c) => c.type === "client-side"),
      }))
      .filter((sec) => sec.containerIDs.length > 0);

    const serverSections = newSections
      .map((sec) => ({
        ...sec,
        containerIDs: sec.containerIDs.filter((c) => c.type === "server-side"),
      }))
      .filter((sec) => sec.containerIDs.length > 0);

    const updatedParams = {
      ...configParamsToSave,
      tags_inactivity_client: { config_sections: clientSections },
      tags_inactivity_server: { config_sections: serverSections },
    };

    const curCheckFreq = configSections.map((sec) => sec.frequency).join(",");
    updatedParams.check_frequency = curCheckFreq;

    const curCompanyDomainsForSave = Array.isArray(curCompanyDomains)
      ? curCompanyDomains.map((domain) => ({ domain: domain.domain || domain }))
      : [];
    updatedParams.company_domains = curCompanyDomainsForSave;

    if (!_.isEqual(updatedParams, configParamsToSave)) {
      setConfigParamsToSave(updatedParams);
    }
  }, [configSections, configParamsToSave, companyDomains]);

  useEffect(() => {
    setConfigParams(configParamsToSave);
  }, [configParamsToSave, setConfigParams]);

  // Reset function to restore original state on modal close without saving
  const resetChanges = () => {
    setConfigSections(originalConfigSectionsRef.current);
    setCurCompanyDomains(initialCompanyDomainsRef.current);
    setModalSaveStatus(false);
  };

  const addConfigSection = () => {
    const newSection = {
      id: nextSectionIdRef.current++,
      sectionTitle: `Monitoring Rule ${configSections.length + 1}`,
      isActive: true,
      containerIDs: [],
      domains: [],
      tagDetails: [{ tagType: "tag name", matchType: "exactly matches", value: "" }],
      trigger: [{ type: "hostname", matchType: "exactly matches", value: "" }],
      frequency: "hourly",
    };
    setConfigSections([...configSections, newSection]);
    setModalSaveStatus(true);
  };

  const updateSection = (index, key, value) => {
    const updated = [...configSections];
    updated[index][key] = value;
    setConfigSections(updated);
    setModalSaveStatus(true);
  };

  const updateTagDetails = (index, key, value) => {
    const updated = [...configSections];
    updated[index].tagDetails[0][key] = value;
    setConfigSections(updated);
    setModalSaveStatus(true);
  };

  const updateTrigger = (index, key, value) => {
    const updated = [...configSections];
    updated[index].trigger[0][key] = value;
    setConfigSections(updated);
    setModalSaveStatus(true);
  };

  const duplicateSection = (index) => {
    const newSection = {
      ...configSections[index],
      id: nextSectionIdRef.current++,
      sectionTitle: `${configSections[index].sectionTitle} (Copy)`,
    };
    setConfigSections([...configSections, newSection]);
    setModalSaveStatus(true);
  };

  const removeSection = (index) => {
    if (configSections.length === 1) {
      const resetSection = {
        ...configSections[0],
        containerIDs: [],
        domains: [],
        tagDetails: [{ tagType: "", matchType: "", value: "" }],
        trigger: [{ type: "", matchType: "", value: "" }],
        frequency: checkFrequency || "hourly",
      };
      setConfigSections([resetSection]);
    } else {
      setConfigSections(configSections.filter((_, i) => i !== index));
    }
    setModalSaveStatus(true);
  };

  const toggleSectionActive = (index) => {
    const updated = [...configSections];
    updated[index].isActive = !updated[index].isActive;
    setConfigSections(updated);
    setModalSaveStatus(true);
  };

  const containerOptions = useMemo(() => {
    const clientOptions = Array.isArray(gtmContainers?.client)
      ? gtmContainers.client
        .filter((c) => c.container)
        .map((c, idx) => ({
          label: `${c.container} (client)`,
          value: c.container,
          type: "client-side",
          key: `client-${idx}`,
        }))
      : [];
    const serverOptions = Array.isArray(gtmContainers?.server)
      ? gtmContainers.server
        .filter((c) => c.container)
        .map((c, idx) => ({
          label: `${c.container} (server)`,
          value: c.container,
          type: "server-side",
          key: `server-${idx}`,
        }))
      : [];
    return [...clientOptions, ...serverOptions];
  }, [gtmContainers]);

  return (
    <div className="modal-elems-rows">
      <div style={{ marginBottom: '15px' }}>
        <h2 className='header-letter-spacing' style={{ marginBottom: '30px' }}>Tag Volume monitoring</h2>
        <div className="container-guide modal-panel-button-row">
          <Link className="modal-guide-banner blue-bg" target="_blank" to={TIImplmenetationLink}>
            <div className="file-icon">
              <svg width="26" height="31" viewBox="0 0 26 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g filter="url(#filter0_d_190_157)">
                  <path d="M2.64922 3.41406H16.4948V7.60147C16.4948 7.95999 16.7855 8.25062 17.144 8.25062H19.3979V26.6164C19.3979 26.975 19.1073 27.2656 18.7488 27.2656H2.64922C2.2907 27.2656 2.00006 26.975 2.00006 26.6164V4.06322C2.00006 3.7047 2.2907 3.41406 2.64922 3.41406Z" fill="#69C4FF" fillOpacity="0.5" />
                  <path d="M3.72516 24.6433H16.4543C16.4543 24.8469 16.6193 25.0119 16.8228 25.0119H19.4347V25.8118C19.4347 26.1703 19.1441 26.4609 18.7856 26.4609H4.37431C4.0158 26.4609 3.72516 26.1703 3.72516 25.8118V24.6433Z" fill="#69C4FF" fillOpacity="0.5" />
                  <path d="M4.34194 1.79309H18.3155V5.98808C18.3155 6.3466 18.6061 6.63724 18.9646 6.63724H21.7393V25.0329C21.7393 25.3914 21.4487 25.682 21.0902 25.682H4.34194C3.98342 25.682 3.69278 25.3914 3.69278 25.0329V2.44225C3.69278 2.08373 3.98342 1.79309 4.34194 1.79309Z" fill="#DBF1FF" />
                  <path d="M5.41306 22.566H18.6682C18.6682 22.755 18.8214 22.9082 19.0104 22.9082H21.7718V23.6046C21.7718 23.9631 21.4811 24.2538 21.1226 24.2538H6.06221C5.70369 24.2538 5.41306 23.9631 5.41306 23.6046V22.566Z" fill="#B0E0FF" />
                  <path d="M6.06221 0.170166H18.7207V4.25985C18.7207 4.61837 19.0114 4.909 19.3699 4.909H23.46V22.6673C23.46 23.1492 23.0694 23.5398 22.5875 23.5398H6.06221C5.70369 23.5398 5.41306 23.2491 5.41306 22.8906V0.819322C5.41306 0.460803 5.70369 0.170166 6.06221 0.170166Z" fill="white" />
                  <line x1="8.82252" y1="9.40248" x2="19.988" y2="9.40248" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                  <line x1="8.90306" y1="15.1451" x2="20.0685" y2="15.1451" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                  <line x1="8.82252" y1="11.3499" x2="19.988" y2="11.3499" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                  <line x1="8.90306" y1="17.0925" x2="20.0685" y2="17.0925" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                  <line x1="8.82252" y1="13.1675" x2="19.988" y2="13.1675" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                  <path d="M19.3703 4.946C19.0118 4.94601 18.7212 4.65537 18.7212 4.29685L18.7212 0.170195L23.4599 4.94597L19.3703 4.946Z" fill="#69C4FF" fillOpacity="0.14" />
                </g>
                <defs>
                  <filter id="filter0_d_190_157" x="0.255097" y="0.170166" width="24.9498" height="30.5854" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                    <feOffset dy="1.74496" />
                    <feGaussianBlur stdDeviation="0.872482" />
                    <feComposite in2="hardAlpha" operator="out" />
                    <feColorMatrix type="matrix" values="0 0 0 0 0.168627 0 0 0 0 0.168627 0 0 0 0 0.709804 0 0 0 0.15 0" />
                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_190_157" />
                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_190_157" result="shape" />
                  </filter>
                </defs>
              </svg>

            </div>
            <div className="panel-button-text">
              <div className="panel-button-desc">Learn how to set up and optimize tag inactivity configuration</div>
            </div>
          </Link>
        </div>
        <div style={{ marginTop: '15px' }}>
          <p style={{ margin: 0 }}>
            Set up Tag volume monitoring via the rules below.
            Need help? Check our {" "}
            <Link target="_blank" to={TIImplmenetationLink}>
              detailed guide
            </Link>.
          </p>
        </div>
      </div>

      <div className="modal-elems-rows">
        {configSections.map((section, index) => {
          const uniqueKey = section.id ?? `section-${index}`;
          const itemsForSection = [
            {
              key: `panel-${uniqueKey}`,
              label: (
                <div className="d-flex justify-content-between align-items-center" >
                  <Input
                    value={section.sectionTitle}
                    onChange={(e) => updateSection(index, "sectionTitle", e.target.value)}
                    placeholder="Monitoring Rule Title"
                    style={{
                      width: "70%",
                      fontWeight: "bold",
                      fontSize: "16px",
                      border: "none",
                      outline: "none",
                    }}
                  />
                  <div>
                    <Button
                      type="text"
                      style={{
                        color: section.isActive ? colors.ccBlue : "gray",
                        opacity: section.isActive ? 1 : 0.5,
                        padding: 0,
                      }}
                      onClick={() => duplicateSection(index)}
                      icon={<CopyOutlined />}
                    />
                    <Button
                      type="text"
                      style={{
                        color: section.isActive ? colors.ccBlue : "gray",
                        opacity: section.isActive ? 1 : 0.5,
                        padding: 0,
                      }}
                      onClick={() => toggleSectionActive(index)}
                      icon={
                        section.isActive ? <PauseCircleOutlined /> : <PlayCircleOutlined />
                      }
                    />
                    <Popconfirm
                      title="Are you sure you want to delete this rule?"
                      onConfirm={() => removeSection(index)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button
                        type="text"
                        style={{
                          color: section.isActive ? colors.ccBlue : "gray",
                          opacity: section.isActive ? 1 : 0.5,
                          padding: 0,
                        }}
                        icon={<DeleteOutlined />}
                      />
                    </Popconfirm>
                  </div>
                </div>
              ),
              children: (
                <>
                  {/* Container and Domain selection */}
                  <div
                    className="inputs-row-3-col-single d-flex flex-row "
                    style={{ position: "relative", marginBottom: 0 }}
                  >
                    <div className="mb-3" style={{ marginRight: "1rem" }}>
                      <h3
                        className="modal-category fw-bold mb-2"
                        style={{ fontSize: "14px" }}
                      >
                        Container ID
                        <InfoCircleFilled
                          size={13}
                          className="info-icon"
                          onMouseEnter={() => setShowGuideDivGTM(true)}
                          onMouseLeave={() => setShowGuideDivGTM(false)}
                        />
                      </h3>
                      {showGuideDivGTM && (
                        <div className="hoverable-div-parent" style={{ left: "8em" }}>
                          <p className="hover-content">
                            Select the GTM container where the rule should apply.
                            Only the ID's added to the configurations are shown.
                          </p>
                        </div>
                      )}

                      <Select
                        mode="multiple"
                        placeholder="GTM-XXXX(client)"
                        labelInValue
                        maxTagCount={1}
                        maxTagTextLength="responsive"
                        maxTagPlaceholder={() => "..."}
                        style={{
                          width: "100%",
                          minWidth: "14rem",
                          maxWidth: "14rem",
                          marginRight: "2rem",
                        }}
                        dropdownStyle={{ maxWidth: "14rem" }}
                        options={containerOptions}
                        popupClassName="custom-dropdown"
                        value={
                          Array.isArray(section.containerIDs)
                            ? section.containerIDs.map((obj, idx) => ({
                              key: `${obj.value}-${idx}`,
                              label: obj.label,
                              value: obj.value,
                            }))
                            : []
                        }
                        onChange={(selectedItems, optionObjects) => {
                          const newContainers = optionObjects.map((opt) => ({
                            value: opt.value,
                            label: opt.label,
                            type: opt.type,
                          }));
                          updateSection(index, "containerIDs", newContainers);
                        }}
                      />
                    </div>

                    <div className="mb-3" style={{ position: "relative", marginBottom: 0 }}>
                      <h3 className="modal-category mb-2" style={{ fontSize: "14px" }}>
                        Domain
                        <InfoCircleFilled
                          size={13}
                          className="info-icon"
                          onMouseEnter={() => setShowGuideDiv(true)}
                          onMouseLeave={() => setShowGuideDiv(false)}
                        />
                      </h3>
                      {showGuideDiv && (
                        <div className="hoverable-div-parent" style={{ left: "7em" }}>
                          <p className="hover-content">
                            Specify which domains to monitor. Only the domains
                            added to the general settings are available.
                          </p>
                        </div>
                      )}
                      <Select
                        mode="multiple"
                        placeholder="code-cube.io"
                        maxTagCount={1}
                        maxTagTextLength={20}
                        style={{ width: "100%", minWidth: "14rem" }}
                        popupClassName="custom-dropdown"
                        options={
                          Array.isArray(companyDomains)
                            ? companyDomains.map((domain) => ({
                              value: domain.domain,
                              label: domain.domain,
                            }))
                            : []
                        }
                        value={section.domains}
                        onChange={(value) => updateSection(index, "domains", value)}
                      />
                    </div>
                  </div>

                  {/* Tag details */}
                  <div className="mb-3" style={{ position: "relative", marginBottom: 0 }}>
                    <h3 className="modal-category mb-2" style={{ fontSize: "14px" }}>
                      Tag details
                      <InfoCircleFilled
                        size={13}
                        className="info-icon"
                        onMouseEnter={() => setShowGuideDivTD(true)}
                        onMouseLeave={() => setShowGuideDivTD(false)}
                      />
                      {showGuideDivTD && (
                        <div className="hoverable-div-parent" style={{ left: "7em" }}>
                          <p className="hover-content">Enter the tag name or ID to monitor.</p>
                        </div>
                      )}
                    </h3>
                    <div className="d-flex">
                      <Select
                        style={{ minWidth: "10rem", marginRight: "15px" }}
                        placeholder="tag name"
                        value={section.tagDetails[0].tagType}
                        onChange={(val) => updateTagDetails(index, "tagType", val)}
                        popupClassName="custom-dropdown"
                        options={[
                          { value: "tag name", label: "tag name" },
                          { value: "tag ID", label: "tag ID" },
                        ]}
                      />
                      <Select
                        style={{ minWidth: "10rem", marginRight: "15px" }}
                        placeholder="Match Type"
                        value={section.tagDetails[0].matchType}
                        onChange={(val) => updateTagDetails(index, "matchType", val)}
                        popupClassName="custom-dropdown"
                        options={[
                          { value: "exactly matches", label: "exactly matches" },
                          { value: "matches regex", label: "matches regex" },
                          { value: "contains", label: "contains" },
                        ]}
                      />
                      <Input
                        placeholder="ga4 - preview"
                        value={section.tagDetails[0].value}
                        onChange={(e) => updateTagDetails(index, "value", e.target.value)}
                        style={{ minWidth: "14rem" }}
                      />
                    </div>
                  </div>

                  {/* Optional trigger */}
                  <div className="mb-3" style={{ position: "relative", marginBottom: 0 }}>
                    <h3 className="modal-category mb-2" style={{ display: "inline", fontSize: "14px" }}>
                      Trigger{" "}
                      <p
                        className="fw-bold"
                        style={{ display: "inline", color: "#D3D3D3", fontSize: "10px" }}
                      >
                        (optional)
                      </p>{" "}
                      <InfoCircleFilled
                        size={13}
                        className="info-icon"
                        style={{ marginLeft: '10px' }}
                      />
                    </h3>
                    <div className="d-flex">
                      <Select
                        style={{ minWidth: "10rem", marginRight: "15px" }}
                        placeholder="/tag-monitor"
                        value={section.trigger[0].type}
                        onChange={(val) => updateTrigger(index, "type", val)}
                        popupClassName="custom-dropdown"
                        options={[
                          { value: "page path", label: "page path" },
                          { value: "full url", label: "full url" },
                        ]}
                      />
                      <Select
                        style={{ minWidth: "10rem", marginRight: "15px" }}
                        placeholder="Match Type"
                        value={section.trigger[0].matchType}
                        onChange={(val) => updateTrigger(index, "matchType", val)}
                        popupClassName="custom-dropdown"
                        options={[
                          { value: "exactly matches", label: "exactly matches" },
                          { value: "matches regex", label: "matches regex" },
                          { value: "contains", label: "contains" },
                        ]}
                      />
                      <Input
                        placeholder="/tag-monitor"
                        value={section.trigger[0].value}
                        onChange={(e) => updateTrigger(index, "value", e.target.value)}
                        style={{ minWidth: "14rem" }}
                      />
                    </div>
                  </div>

                  {/* Frequency */}
                  <div className="mb-3" style={{ position: "relative", marginBottom: 0 }}>
                    <h3 className="modal-category mb-2" style={{ fontSize: "14px" }}>
                      Frequency
                      <InfoCircleFilled
                        size={13}
                        className="info-icon"
                        onMouseEnter={() => setShowGuideDivFrequency(true)}
                        onMouseLeave={() => setShowGuideDivFrequency(false)}
                      />
                      {showGuideDivFrequency && (
                        <div className="hoverable-div-parent" style={{ left: "7em" }}>
                          <p className="hover-content">
                            Set the monitoring interval. For critical tags,
                            we recommend checking every hour. For less crucial tags,
                            daily checks may suffice.
                          </p>
                        </div>
                      )}
                    </h3>
                    <Select
                      placeholder="every hour"
                      value={section.frequency || "every hour"}
                      onChange={(value) => updateSection(index, "frequency", value)}
                      style={{ width: "10rem" }}
                      popupClassName="custom-dropdown"
                      options={[
                        { value: "hourly", label: "every hour" },
                        { value: "every2hours", label: "every 2 hours" },
                        { value: "every4hours", label: "every 4 hours" },
                        { value: "every12hours", label: "every 12 hours" },
                        { value: "daily", label: "every 24 hours" },
                      ]}
                    />
                  </div>
                </>
              ),
            },
          ];

          return (
            <div key={uniqueKey} style={{ width: "100%", maxWidth: "40rem" }}>
              <Collapse
                defaultActiveKey={[`panel-${uniqueKey}`]}
                accordion
                ghost
                expandIconPosition="end"
                style={{
                  border: "1.5px solid",
                  borderColor: colors.ccBlue,
                  color: section.isActive ? colors.ccBlue : "gray",
                  opacity: section.isActive ? 1 : 0.5,
                }}
                className="custom-collapse"
                expandIcon={({ isActive }) => (
                  <Button
                    type="text"
                    style={{
                      transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.3s",
                      color: colors.ccBlue,
                      marginTop: "14px",
                      padding: 0
                    }}
                  >
                    <ChevronDown size={20} strokeWidth={4} />
                  </Button>
                )}
                items={itemsForSection.map(item => ({
                  ...item,
                  children: <div style={{ padding: '10px' }}>{item.children}</div>
                }))}
              />
            </div>
          );
        })}

        <Button
          type="dashed"
          onClick={addConfigSection}
          style={{
            width: "100%",
            maxWidth: "40rem",
            color: colors.ccBlue,
            borderColor: colors.ccBlue,
            borderStyle: "dashed",
            borderWidth: "2px",
            fontWeight: "bold"
          }}
        >
          + Add monitoring rule
        </Button>
      </div>
    </div>
  );
};


export const CloudMonitoringSettingsModal = ({ context }) => {
  const {
    cloudRunData,
    companyInfo,
    companyId,
    setModalSaveStatus,
  } = useContext(context);

  const tagMonitorSubscription = companyInfo["tag_monitor_subscription_model"];
  const googleCloudImplementationDocsLink = "https://docs.code-cube.io/tag-monitor/configuration/monitor-cloud-run/";

  const [googleCloudDataForm, setGoogleCloudDataForm] = useState({});
  const [response, setResponse] = useState({});
  const [loadingActive, setloadingActive] = useState(false);
  const [alertData, setAlertData] = useState({});
  const [formData, setFormData] = useState(cloudRunData);


  useEffect(() => {
    const saveData = async () => {
      setloadingActive(true);
      const resp = await ConfigService.saveGoogleCloudData({ googleCloudDataForm, companyId });
      const respData = await resp?.json() || resp;
      setResponse(respData);
      setloadingActive(false);
      return respData;
    }

    if (Object.keys(googleCloudDataForm).length > 0) saveData();
  }, [googleCloudDataForm]);

  useEffect(() => {
    setGoogleCloudDataForm({});
    setResponse({});
    setloadingActive(false);
    setAlertData({});
    setFormData(cloudRunData);
  }, [companyId, cloudRunData]);

  useEffect(() => {
    const error = response.error;
    const details = response.details == "None" ? "" : response.details;
    const success = response.success;
    const type = success ? "success" : "error";
    const message = success ? "Successfull implementation!" : "Error occured!";

    setAlertData(
      {
        'error': error,
        'details': details,
        'type': type,
        'message': message
      }
    );
  }, [response]);


  return (
    <div className="modal-elems-rows">
      <div id='cloud-run-div'>
        <div>
          <h2 className='header-letter-spacing'>Cloud Run monitoring</h2>
        </div>

        <div style={{ marginTop: '15px', marginBottom: '15px' }}>
          <div className="container-guide modal-panel-button-row">
            <Link className="modal-guide-banner blue-bg" target="_blank" to={googleCloudImplementationDocsLink}>
              <div className="file-icon">
                <svg width="26" height="31" viewBox="0 0 26 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g filter="url(#filter0_d_190_157)">
                    <path d="M2.64922 3.41406H16.4948V7.60147C16.4948 7.95999 16.7855 8.25062 17.144 8.25062H19.3979V26.6164C19.3979 26.975 19.1073 27.2656 18.7488 27.2656H2.64922C2.2907 27.2656 2.00006 26.975 2.00006 26.6164V4.06322C2.00006 3.7047 2.2907 3.41406 2.64922 3.41406Z" fill="#69C4FF" fillOpacity="0.5" />
                    <path d="M3.72516 24.6433H16.4543C16.4543 24.8469 16.6193 25.0119 16.8228 25.0119H19.4347V25.8118C19.4347 26.1703 19.1441 26.4609 18.7856 26.4609H4.37431C4.0158 26.4609 3.72516 26.1703 3.72516 25.8118V24.6433Z" fill="#69C4FF" fillOpacity="0.5" />
                    <path d="M4.34194 1.79309H18.3155V5.98808C18.3155 6.3466 18.6061 6.63724 18.9646 6.63724H21.7393V25.0329C21.7393 25.3914 21.4487 25.682 21.0902 25.682H4.34194C3.98342 25.682 3.69278 25.3914 3.69278 25.0329V2.44225C3.69278 2.08373 3.98342 1.79309 4.34194 1.79309Z" fill="#DBF1FF" />
                    <path d="M5.41306 22.566H18.6682C18.6682 22.755 18.8214 22.9082 19.0104 22.9082H21.7718V23.6046C21.7718 23.9631 21.4811 24.2538 21.1226 24.2538H6.06221C5.70369 24.2538 5.41306 23.9631 5.41306 23.6046V22.566Z" fill="#B0E0FF" />
                    <path d="M6.06221 0.170166H18.7207V4.25985C18.7207 4.61837 19.0114 4.909 19.3699 4.909H23.46V22.6673C23.46 23.1492 23.0694 23.5398 22.5875 23.5398H6.06221C5.70369 23.5398 5.41306 23.2491 5.41306 22.8906V0.819322C5.41306 0.460803 5.70369 0.170166 6.06221 0.170166Z" fill="white" />
                    <line x1="8.82252" y1="9.40248" x2="19.988" y2="9.40248" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                    <line x1="8.90306" y1="15.1451" x2="20.0685" y2="15.1451" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                    <line x1="8.82252" y1="11.3499" x2="19.988" y2="11.3499" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                    <line x1="8.90306" y1="17.0925" x2="20.0685" y2="17.0925" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                    <line x1="8.82252" y1="13.1675" x2="19.988" y2="13.1675" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                    <path d="M19.3703 4.946C19.0118 4.94601 18.7212 4.65537 18.7212 4.29685L18.7212 0.170195L23.4599 4.94597L19.3703 4.946Z" fill="#69C4FF" fillOpacity="0.14" />
                  </g>
                  <defs>
                    <filter id="filter0_d_190_157" x="0.255097" y="0.170166" width="24.9498" height="30.5854" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                      <feFlood floodOpacity="0" result="BackgroundImageFix" />
                      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                      <feOffset dy="1.74496" />
                      <feGaussianBlur stdDeviation="0.872482" />
                      <feComposite in2="hardAlpha" operator="out" />
                      <feColorMatrix type="matrix" values="0 0 0 0 0.168627 0 0 0 0 0.168627 0 0 0 0 0.709804 0 0 0 0.15 0" />
                      <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_190_157" />
                      <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_190_157" result="shape" />
                    </filter>
                  </defs>
                </svg>

              </div>
              <div className="panel-button-text">
                <div className="panel-button-desc">Guide how to set up the configuration</div>
              </div>
            </Link>
          </div>

          <div style={{ marginTop: '15px' }}>
            <p style={{ margin: 0 }}>
              Enable advanced monitoring for your Google Tag Manager server hosted on Cloud Run via the three steps below. The following will be monitored:
            </p>
            <ul style={{ marginLeft: 0 }}>
              <li>Real-time monitoring of the health of your Cloud Run instances</li>
              <li>Detailed error tracking and alerts for server-side tagging errors</li>
            </ul>
          </div>
        </div>

        <GoogleCloudConfigForm
          cloudRunData={formData}
          saveFunction={setGoogleCloudDataForm}
          tagMonitorSubscription={tagMonitorSubscription}
          setModalSaveStatus={setModalSaveStatus}
        />

        {loadingActive && (
          <Flex align="center" gap="middle">
            <Spin indicator={<LoadingOutlined style={{ fontSize: 48, marginBottom: "1rem" }} spin />} />
          </Flex>
        )}

        {
          Object.keys(response).length > 0 && !loadingActive &&
          <Alert
            message={alertData.message}
            description={alertData.details}
            type={alertData.type}
          />
        }
      </div>
    </div>
  );
}
